import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faUser, faHistory, faUsersCog, faChartBar, faSignOutAlt, faCog, faShieldAlt, faUserMd, faTools } from '@fortawesome/free-solid-svg-icons';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Login } from './components/Auth/Login';
import { AccountActivation } from './components/Auth/AccountActivation';
import { PasswordReset } from './components/Auth/PasswordReset';
import { ChatComparison } from './components/Chat/ChatComparison';
import { UserProfile } from './components/Chat/subcomponents/UserProfile';
import { ChatHistory } from './components/Chat/subcomponents/ChatHistory';
import { PatientManagement } from './components/Admin/PatientManagement';
import { DoctorManagement } from './components/Admin/DoctorManagement';
import { AdminStatistics } from './components/Admin/AdminStatistics';
import { AdminSettings } from './components/Admin/AdminSettings';
import SecurityManagement from './components/Admin/SecurityManagement';
import { Toolbox } from './components/Toolbox/Toolbox';
import './App.css';
import { Footer } from './Footer';

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();

  if (!user) return null;

  return (
    <nav className="main-nav">
      <div className="nav-brand">
        <img src="/logo-radonek.png" alt="Radonek Logo" className="nav-logo" height={70} />
        <div className="brand-text">
          <h2>Radonek</h2>
          <span className="brand-subtitle">Asystent pacjenta</span>
        </div>
      </div>
      
      <div className="nav-center">
        <span className="user-info">
          {user.full_name} 
          {user.is_admin && <span className="admin-badge">Admin</span>}
          {user.is_doctor && <span className="doctor-badge">Lekarz</span>}
        </span>
      </div>

      <div className="nav-links">
        {user.is_admin ? (
          <>
            <a href="/admin/patients" className="nav-link"><FontAwesomeIcon icon={faUsersCog} /> Pacjenci</a>
            <a href="/admin/doctors" className="nav-link"><FontAwesomeIcon icon={faUserMd} /> Lekarze</a>
            <a href="/toolbox" className="nav-link"><FontAwesomeIcon icon={faTools} /> Toolbox AI</a>
            <a href="/admin/statistics" className="nav-link"><FontAwesomeIcon icon={faChartBar} /> Statystyki</a>
            <a href="/admin/security" className="nav-link"><FontAwesomeIcon icon={faShieldAlt} /> Bezpieczeństwo</a>
            <a href="/admin/settings" className="nav-link"><FontAwesomeIcon icon={faCog} /> Ustawienia</a>
          </>
        ) : user.is_doctor ? (
          <>
            <a href="/admin/patients" className="nav-link"><FontAwesomeIcon icon={faUsersCog} /> Moi Pacjenci</a>
            <a href="/toolbox" className="nav-link"><FontAwesomeIcon icon={faTools} /> Toolbox AI</a>
            <a href="/admin/statistics" className="nav-link"><FontAwesomeIcon icon={faChartBar} /> Statystyki</a>
          </>
        ) : (
          <>
            <a href="/chat" className="nav-link"><FontAwesomeIcon icon={faComments} /> Chat</a>
            <a href="/profile" className="nav-link"><FontAwesomeIcon icon={faUser} /> Profil</a>
            <a href="/history" className="nav-link"><FontAwesomeIcon icon={faHistory} /> Historia</a>
          </>
        )}
        <button onClick={logout} className="logout-button"><FontAwesomeIcon icon={faSignOutAlt} /> Wyloguj</button>
      </div>
    </nav>
  );
};

const ProtectedRoute: React.FC<{ 
  children: React.ReactNode; 
  adminOnly?: boolean;
  doctorOrAdminOnly?: boolean;
  patientOnly?: boolean;
}> = ({ 
  children, 
  adminOnly = false,
  doctorOrAdminOnly = false,
  patientOnly = false
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Ładowanie...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to={user.is_doctor ? "/admin/patients" : "/chat"} replace />;
  }

  if (doctorOrAdminOnly && !(user.is_admin || user.is_doctor)) {
    return <Navigate to="/chat" replace />;
  }

  if (patientOnly && (user.is_admin || user.is_doctor)) {
    return <Navigate to={user.is_admin ? "/admin/patients" : "/admin/patients"} replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Ładowanie...</div>;
  }

  return (
    <div className="app">
      <Navigation />
      <main className="main-content">
        <Routes>
          <Route path="/login" element={
            user ? <Navigate to={user.is_admin ? "/admin/patients" : user.is_doctor ? "/admin/patients" : "/chat"} replace /> : <Login />
          } />
          <Route path="/activate" element={
            user ? <Navigate to={user.is_admin ? "/admin/patients" : user.is_doctor ? "/admin/patients" : "/chat"} replace /> : <AccountActivation />
          } />
          <Route path="/password-reset" element={
            user ? <Navigate to={user.is_admin ? "/admin/patients" : user.is_doctor ? "/admin/patients" : "/chat"} replace /> : <PasswordReset />
          } />
          <Route path="/chat" element={
            <ProtectedRoute patientOnly>
              <ChatComparison />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute patientOnly>
              <UserProfile />
            </ProtectedRoute>
          } />
          <Route path="/history" element={
            <ProtectedRoute patientOnly>
              <ChatHistory />
            </ProtectedRoute>
          } />
          <Route path="/toolbox" element={
            <ProtectedRoute doctorOrAdminOnly>
              <Toolbox />
            </ProtectedRoute>
          } />
          <Route path="/admin/patients" element={
            <ProtectedRoute doctorOrAdminOnly>
              <PatientManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/doctors" element={
            <ProtectedRoute adminOnly>
              <DoctorManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/statistics" element={
            <ProtectedRoute adminOnly>
              <AdminStatistics />
            </ProtectedRoute>
          } />
          <Route path="/admin/security" element={
            <ProtectedRoute adminOnly>
              <SecurityManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute adminOnly>
              <AdminSettings />
            </ProtectedRoute>
          } />
          <Route path="/" element={
            <Navigate to={user ? (user.is_admin ? "/admin/patients" : user.is_doctor ? "/admin/patients" : "/chat") : "/login"} replace />
          } />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;
